import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { useSession } from "@clerk/clerk-react";
import axios from "axios";
import { DiamondIcon, LoaderCircle } from "lucide-react";
import { toast } from "sonner";
import { ClickableIcon } from "./TailwindStylingPanel/components/input";
import {
  ControlEditor,
  ControlsSpec,
  ControlValue,
  ControlValues,
} from "../ControlEditor";
import { selectSelectedTempoElement } from "../../store/iframeSlice";
import {
  useActiveCanvas,
  useCanvasId,
  getUnexpiredToken,
} from "../../utils/utils";
import { useDesignCommandManager } from "@/store/zustand/designCommandManagerStore";
import { ChangePropChange } from "@/store/zustand/changeLedgerTypes";
import { useCodebaseStore } from "../../store/zustand/codebaseStore";
import { TempoElement } from "../../utils/tempoElement";
import { JsxElement } from "../../utils/jsxElementTypes";
import { MinusIcon, PlusIcon } from "./TailwindStylingPanel/components/icons";

const shouldFilterOutProp = (propName: string) => {
  return propName.startsWith("aria-") || propName === "dangerouslySetInnerHTML";
};

// Raw prop format returned by backend
type PropValue =
  | string
  | number
  | boolean
  | PropValue[]
  | { [key: string]: PropValue };
interface ParsedPropBase {
  required?: boolean;
  defaultValue?: PropValue;
}
type ParsedProp =
  | ({ type: "string"; value?: string } & ParsedPropBase)
  | ({ type: "number"; value?: number } & ParsedPropBase)
  | ({ type: "boolean"; value?: boolean } & ParsedPropBase)
  | ({
      type: "union";
      values: (string | number)[];
      value?: string | number;
    } & ParsedPropBase);

// Convert ParsedProp map to ControlEditor spec + initial values
const convertParsedPropsToControlSpec = (
  parsedProps: Record<string, ParsedProp>
): { controls: ControlsSpec; values: ControlValues } => {
  const controls: ControlsSpec = {};
  const values: ControlValues = {};
  Object.entries(parsedProps).forEach(([name, prop]) => {
    switch (prop.type) {
      case "string":
        controls[name] = { type: "text" };
        values[name] = (prop.value ?? "") as string;
        break;
      case "number":
        controls[name] = { type: "number" };
        values[name] = (prop.value ?? 0) as number;
        break;
      case "boolean":
        controls[name] = { type: "boolean" };
        values[name] = (prop.value ?? false) as boolean;
        break;
      case "union":
        controls[name] = { type: "select", options: prop.values };
        values[name] = (prop.value ?? prop.values[0]) as ControlValue;
        break;
    }
  });
  return { controls, values };
};

// Hook to fetch, cache, and update component props
const useComponentProps = () => {
  const selectedElement: TempoElement = useSelector(selectSelectedTempoElement);
  const codebaseId = selectedElement?.codebaseId;
  const selectedTreeElement: JsxElement | undefined = useCodebaseStore((s) =>
    codebaseId ? s.treeElementLookup[codebaseId] : undefined
  );

  const canvasId = useCanvasId();
  const { session } = useSession();
  const executeCommand = useDesignCommandManager((s) => s.executeCommand);

  // Cached controls in store
  const cachedSpec = useCodebaseStore((s) =>
    codebaseId ? s.cachedControlsSpecs[codebaseId] : undefined
  );
  const setCachedSpecs = useCodebaseStore((s) => s.setCachedControls);
  const cachedValues = useCodebaseStore((s) =>
    codebaseId ? s.cachedControlsValues[codebaseId] : undefined
  );
  const setCachedValues = useCodebaseStore((s) => s.setCachedControlsValues);

  // Store controls and values as records keyed by codebaseId
  const [controlsRecord, setControlsRecord] = useState<
    Record<string, ControlsSpec>
  >({});
  const [valuesRecord, setValuesRecord] = useState<
    Record<string, ControlValues>
  >({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get current codebaseId-specific values, or empty if no codebaseId
  const controls = codebaseId
    ? controlsRecord[codebaseId] || cachedSpec || null
    : null;
  const values = codebaseId
    ? valuesRecord[codebaseId] || cachedValues || {}
    : {};

  const displayName = selectedTreeElement?.componentName;

  // Fetch on element change
  useEffect(() => {
    setLoading(true);

    const fetchProps = async () => {
      // componentSourceImport doesn't have the .tsx extension
      let filepath =
        selectedTreeElement?.componentSourceImport?.filename ||
        selectedTreeElement?.filename ||
        "";
      if (!filepath.endsWith(".tsx")) {
        filepath = filepath + ".tsx";
      }

      // Should have a name and should be one of the files we've loaded for the codebase (not any external library file)
      if (!filepath || !selectedTreeElement?.componentName || !codebaseId) {
        // Clear controls and values for this codebaseId if it exists
        if (codebaseId) {
          setControlsRecord((prev) => ({ ...prev, [codebaseId]: {} }));
          setValuesRecord((prev) => ({ ...prev, [codebaseId]: {} }));
        }
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const supabaseJWT = await getUnexpiredToken(session);

        const componentName =
          selectedTreeElement?.componentSourceImport?.name ||
          selectedTreeElement.componentName;
        const response = await axios.post(
          `${
            import.meta.env.VITE_BACKEND_BASE_URL
          }/canvases/${canvasId}/parseAndMutate/parse/parseComponentProps`,
          { filepath, componentName },
          { headers: { Authorization: `Bearer ${supabaseJWT}` } }
        );
        const { props } = response.data as {
          props: Record<string, Omit<ParsedProp, "value">> | undefined;
        };

        // merge default + current values
        const parsedMap: Record<string, ParsedProp> = {};
        Object.entries(props ?? {})
          .filter(([key]) => !shouldFilterOutProp(key))
          .forEach(([key, p]) => {
            const v = selectedTreeElement.propLocations?.[key]?.value;
            // Type-safe assignment based on prop type
            if (p.type === "string") {
              parsedMap[key] = {
                ...p,
                value: typeof v === "string" ? v : undefined,
              } as ParsedProp;
            } else if (p.type === "number") {
              parsedMap[key] = {
                ...p,
                value: typeof v === "number" ? v : undefined,
              } as ParsedProp;
            } else if (p.type === "boolean") {
              parsedMap[key] = {
                ...p,
                value: typeof v === "boolean" ? v : undefined,
              } as ParsedProp;
            } else if (p.type === "union") {
              parsedMap[key] = {
                ...p,
                value:
                  typeof v === "string" || typeof v === "number"
                    ? v
                    : undefined,
              } as ParsedProp;
            }
          });
        const { controls: spec, values: init } =
          convertParsedPropsToControlSpec(parsedMap);

        // Store in records keyed by codebaseId
        setControlsRecord((prev) => ({ ...prev, [codebaseId]: spec }));
        setValuesRecord((prev) => ({ ...prev, [codebaseId]: init }));
        setCachedSpecs(codebaseId, spec);
        setCachedValues(codebaseId, init);
      } catch (e) {
        console.error("Failed to load component props:", e);
        setError("Failed to load component props");
        // Clear for this codebaseId on error
        if (codebaseId) {
          setControlsRecord((prev) => ({ ...prev, [codebaseId]: {} }));
          setValuesRecord((prev) => ({ ...prev, [codebaseId]: {} }));
        }
      } finally {
        setLoading(false);
      }
    };
    fetchProps();
  }, [
    selectedTreeElement?.componentName,
    selectedTreeElement?.filename,
    canvasId,
    session,
    codebaseId,
  ]);

  // Change handler
  const handleChange = useCallback(
    (changes: Record<string, ControlValue>) => {
      const propName = Object.keys(changes)[0];
      const value = changes[propName];
      if (!propName || !codebaseId) return;

      // Exit early if value is unchanged
      const currentValue = values[propName];
      if (currentValue === value) {
        return;
      }

      // optimistic update for this specific codebaseId
      setValuesRecord((prev) => ({
        ...prev,
        [codebaseId]: { ...prev[codebaseId], [propName]: value },
      }));

      try {
        executeCommand(
          new ChangePropChange({
            codebaseIdToChange: codebaseId,
            propName,
            propValue: value,
          })
        );
      } catch (e) {
        console.error("Failed to change prop:", e);
        // Rollback optimistic update
        setValuesRecord((prev) => ({
          ...prev,
          [codebaseId]: { ...prev[codebaseId], [propName]: currentValue },
        }));
        toast.error("Failed to update prop");
      }
    },
    [codebaseId, executeCommand, values] // Add values to dependency array
  );

  return {
    controls,
    values,
    displayName,
    loading,
    error,
    handleChange,
  } as const;
};

// Component
export const PropManager: React.FC = () => {
  const { controls, values, displayName, loading, error, handleChange } =
    useComponentProps();
  const [open, setOpen] = useState(false);
  const hasProps = controls && Object.keys(controls).length > 0;

  return (
    <div className="w-full py-2 space-y-2 props-manager">
      {/* Header */}
      <div className="flex flex-row justify-between items-center box-border">
        <div className="flex items-center gap-2">
          {loading && controls === null ? (
            <LoaderCircle
              className="w-3 h-3 flex-shrink-0 animate-spin"
              style={{ color: "var(--primary-n-600)" }}
            />
          ) : (
            <DiamondIcon
              className="w-3 h-3 flex-shrink-0"
              style={{ color: "var(--primary-n-600)" }}
            />
          )}
          <div
            className="text-xs font-medium"
            style={{ color: "var(--primary-n-600)" }}
          >
            {displayName || "Component"}
          </div>
        </div>
        <div
          className="flex flex-col items-center justify-center w-1/12 text-muted-foreground h-3"
          style={{ visibility: hasProps ? "visible" : "hidden" }}
          onClick={() => setOpen((p) => !p)}
        >
          <ClickableIcon>{open ? <MinusIcon /> : <PlusIcon />}</ClickableIcon>
        </div>
      </div>

      {open && (
        <div className="mt-3 py-2 controls-manager space-y-2">
          {error ? (
            <div className="text-[10px] text-red-400">{error}</div>
          ) : loading && controls === null ? (
            <div className="text-[10px] text-[#9F9FA4]">Loading props...</div>
          ) : !hasProps ? (
            <div className="text-[10px] text-[#9F9FA4]">
              No editable props found for this component
            </div>
          ) : (
            <ControlEditor
              controls={controls}
              values={values}
              onChange={handleChange}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default PropManager;
